import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    ArrowLeft,
    Flag,
    GripVertical,
    ChevronRight,
    CheckCircle,
    Circle,
} from 'lucide-react';
import { useAppState, Task } from '../../contexts/AppContext';

type PriorityLane = 'high' | 'medium' | 'normal';

const PRIORITY_CONFIG: Record<PriorityLane, { label: string; color: string; bgColor: string }> = {
    high: { label: 'High Priority', color: '#EF4444', bgColor: '#FEF2F2' },
    medium: { label: 'Medium Priority', color: '#F59E0B', bgColor: '#FFFBEB' },
    normal: { label: 'Normal Priority', color: '#10B981', bgColor: '#ECFDF5' },
};

export default function AppPriorityBoard() {
    const navigate = useNavigate();
    const { tasks, updateTask, toggleTaskComplete } = useAppState();
    const [draggedTask, setDraggedTask] = useState<Task | null>(null);
    const [dropTarget, setDropTarget] = useState<PriorityLane | null>(null);

    // Get active tasks grouped by priority
    const activeTasks = tasks.filter(t => !t.completed && !t.deletedAt);
    const tasksByPriority: Record<PriorityLane, Task[]> = {
        high: activeTasks.filter(t => t.priority === 'high'),
        medium: activeTasks.filter(t => t.priority === 'medium'),
        normal: activeTasks.filter(t => t.priority === 'normal'),
    };

    const handleDragStart = (e: React.DragEvent, task: Task) => {
        setDraggedTask(task);
        e.dataTransfer.effectAllowed = 'move';
        // Add some visual feedback
        if (e.currentTarget instanceof HTMLElement) {
            e.currentTarget.style.opacity = '0.5';
        }
    };

    const handleDragEnd = (e: React.DragEvent) => {
        setDraggedTask(null);
        setDropTarget(null);
        if (e.currentTarget instanceof HTMLElement) {
            e.currentTarget.style.opacity = '1';
        }
    };

    const handleDragOver = (e: React.DragEvent, lane: PriorityLane) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
        setDropTarget(lane);
    };

    const handleDragLeave = () => {
        setDropTarget(null);
    };

    const handleDrop = (e: React.DragEvent, targetPriority: PriorityLane) => {
        e.preventDefault();
        if (draggedTask && draggedTask.priority !== targetPriority) {
            updateTask(draggedTask.id, { priority: targetPriority });
        }
        setDraggedTask(null);
        setDropTarget(null);
    };

    return (
        <div className="min-h-screen bg-[#F5F8FA] dark:bg-dark-bg pb-24">
            {/* Header */}
            <div className="bg-white dark:bg-dark-card border-b border-[#DCE4EA] dark:border-dark-border px-4 py-4">
                <div className="flex items-center gap-3">
                    <button
                        onClick={() => navigate(-1)}
                        className="p-2 -ml-2 hover:bg-gray-100 dark:hover:bg-dark-elevated rounded-full transition-colors"
                    >
                        <ArrowLeft className="w-5 h-5 text-[#1A2832] dark:text-white" />
                    </button>
                    <div>
                        <h1 className="text-lg font-semibold text-[#1A2832] dark:text-white">Command Center</h1>
                        <p className="text-sm text-[#7B8C9B] dark:text-dark-text-muted">Strategic execution & priority management</p>
                    </div>
                </div>
            </div>

            {/* Priority Lanes */}
            <div className="p-4 space-y-4">
                {(Object.keys(PRIORITY_CONFIG) as PriorityLane[]).map(priority => {
                    const config = PRIORITY_CONFIG[priority];
                    const laneTasks = tasksByPriority[priority];
                    const isDropping = dropTarget === priority && draggedTask?.priority !== priority;

                    return (
                        <div
                            key={priority}
                            className={`rounded-2xl transition-all duration-200 ${isDropping
                                ? priority === 'high'
                                    ? 'ring-2 ring-offset-2 ring-red-500'
                                    : priority === 'medium'
                                        ? 'ring-2 ring-offset-2 ring-amber-500'
                                        : 'ring-2 ring-offset-2 ring-green-500'
                                : ''
                                }`}
                            style={{ backgroundColor: config.bgColor }}
                            onDragOver={(e) => handleDragOver(e, priority)}
                            onDragLeave={handleDragLeave}
                            onDrop={(e) => handleDrop(e, priority)}
                        >
                            {/* Lane Header */}
                            <div className="flex items-center gap-2 p-4 pb-2">
                                <Flag className="w-4 h-4" style={{ color: config.color }} />
                                <h2 className="font-semibold text-[#1A2832] dark:text-white">{config.label}</h2>
                                <span
                                    className="ml-auto text-sm font-medium px-2 py-0.5 rounded-full"
                                    style={{ backgroundColor: `${config.color}20`, color: config.color }}
                                >
                                    {laneTasks.length}
                                </span>
                            </div>

                            {/* Tasks */}
                            <div className="px-4 pb-4 space-y-2 min-h-[60px]">
                                {laneTasks.length === 0 ? (
                                    <div className="text-center py-4 text-sm text-[#7B8C9B] dark:text-dark-text-muted border-2 border-dashed border-[#DCE4EA] dark:border-dark-border rounded-xl">
                                        {isDropping ? 'Drop here to set priority' : 'No tasks'}
                                    </div>
                                ) : (
                                    laneTasks.map(task => (
                                        <div
                                            key={task.id}
                                            draggable
                                            onDragStart={(e) => handleDragStart(e, task)}
                                            onDragEnd={handleDragEnd}
                                            className="bg-white dark:bg-dark-card rounded-xl p-3 shadow-sm dark:shadow-dark flex items-center gap-3 cursor-grab active:cursor-grabbing hover:shadow-md transition-shadow"
                                        >
                                            <GripVertical className="w-4 h-4 text-[#DCE4EA] flex-shrink-0" />
                                            <button
                                                onClick={() => toggleTaskComplete(task.id)}
                                                className="flex-shrink-0"
                                            >
                                                {task.completed ? (
                                                    <CheckCircle className="w-5 h-5 text-[#0D5F6B]" />
                                                ) : (
                                                    <Circle className="w-5 h-5 text-[#DCE4EA]" />
                                                )}
                                            </button>
                                            <span className="flex-1 text-sm text-[#1A2832] dark:text-dark-text truncate">
                                                {task.title}
                                            </span>
                                            <button
                                                onClick={() => navigate(`/app/task/${task.id}`)}
                                                className="p-1 hover:bg-gray-100 dark:hover:bg-dark-elevated rounded-full"
                                            >
                                                <ChevronRight className="w-4 h-4 text-[#7B8C9B]" />
                                            </button>
                                        </div>
                                    ))
                                )}

                                {/* Drop indicator when dragging */}
                                {isDropping && (
                                    <div
                                        className="border-2 border-dashed rounded-xl p-3 text-center text-sm"
                                        style={{
                                            borderColor: config.color,
                                            color: config.color,
                                            backgroundColor: `${config.color}10`
                                        }}
                                    >
                                        Drop "{draggedTask?.title}" here
                                    </div>
                                )}
                            </div>
                        </div>
                    );
                })}
            </div>

            {/* Stats Footer */}
            <div className="fixed bottom-20 left-0 right-0 px-4">
                <div className="bg-white dark:bg-dark-card rounded-xl p-3 shadow-lg dark:shadow-dark flex justify-around">
                    <div className="text-center">
                        <div className="text-lg font-bold text-[#EF4444]">{tasksByPriority.high.length}</div>
                        <div className="text-xs text-[#7B8C9B] dark:text-dark-text-muted">High</div>
                    </div>
                    <div className="text-center">
                        <div className="text-lg font-bold text-[#F59E0B]">{tasksByPriority.medium.length}</div>
                        <div className="text-xs text-[#7B8C9B] dark:text-dark-text-muted">Medium</div>
                    </div>
                    <div className="text-center">
                        <div className="text-lg font-bold text-[#10B981]">{tasksByPriority.normal.length}</div>
                        <div className="text-xs text-[#7B8C9B] dark:text-dark-text-muted">Normal</div>
                    </div>
                    <div className="text-center">
                        <div className="text-lg font-bold text-[#0D5F6B]">{activeTasks.length}</div>
                        <div className="text-xs text-[#7B8C9B]">Total</div>
                    </div>
                </div>
            </div>
        </div>
    );
}
